import classNames from 'classnames';
import { Fragment, type ReactNode, useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputFieldTooltip } from './InputFieldTooltip';

const toArray = (inp: string | string[] | undefined) => {
    if (!inp) {
        return [];
    }

    if (Array.isArray(inp)) {
        return inp;
    }

    return [inp];
};

interface TextInputProps {
    label: string;
    disabled?: boolean;
    value?: string;
    className?: string;
    error?: string | string[];
    warning?: string;
    success?: string;
    inputType?: string;
    tooltip?: boolean;
    placeholder?: string;
    title?: string;
    popoverTooltip?: boolean;
    popoverTooltipMessage?: string;
    required?: boolean;
    name?: string;
    id?: string;

    // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
    onChange?(event: any): void;

    // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
    onBlur?(event: any): void;

    // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
    onFocus?(event: any): void;

    // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
    onTooltipClick?(event: any): void;
}

export const TextInput = (props: TextInputProps) => {
    const {
        value,
        label,
        disabled,
        className,
        inputType,
        tooltip,
        popoverTooltip,
        required,
        onChange,
        onBlur,
        onFocus,
        onTooltipClick,
        popoverTooltipMessage,
        name,
        id,
        placeholder: rawPlaceholder,
        title: rawTitle,
    } = props;

    const generatedId = useId();
    const finalId = id || generatedId;

    const intl = useIntl();

    let placeholder: string | undefined;

    if (rawPlaceholder?.startsWith('registration.')) {
        placeholder = intl.formatMessage({ id: rawPlaceholder });
    } else {
        placeholder = rawPlaceholder;
    }

    const title = rawTitle?.startsWith('registration.') ? intl.formatMessage({ id: rawTitle }) : rawTitle;

    const error = toArray(props.error);
    const warning = toArray(props.warning);
    const success = toArray(props.success);

    const cs = {
        'form-group': true,
        'has-error has-feedback': error.length > 0,
        'has-warning has-feedback': warning.length > 0,
        'has-success has-feedback': success.length > 0,
    };

    // quick fix for not showing passwords in error logs
    const successMsgs = [...success]
        .filter(msg => msg && msg.length > 0)
        .reduce((acc: ReactNode[], val: ReactNode) => {
            acc.push(val);
            acc.push(<br />);
            return acc;
        }, []);

    // combine messages
    const otherMsgs: ReactNode[] = [...warning, ...error]
        .filter(msg => msg && msg.length > 0)
        // biome-ignore lint/suspicious/noArrayIndexKey: suppress out of convenience during migration to biome
        .map((msg, id) => <FormattedMessage id={msg} key={id} />)
        .reduce((acc: ReactNode[], val: ReactNode) => {
            acc.push(val);
            acc.push(<br />);
            return acc;
        }, []);

    // biome-ignore lint/suspicious/noArrayIndexKey: suppress out of convenience during migration to biome
    const msgs = successMsgs.concat(otherMsgs).map((node, id) => <Fragment key={id}>{node}</Fragment>);

    return (
        <div className={classNames(className ? { [className]: true } : {})}>
            <div className={classNames(cs)}>
                <label
                    className={'display-inline-flex flex-wrap justify-content-between width-100pct'}
                    htmlFor={finalId}
                >
                    {label.startsWith('registration.') ? <FormattedMessage id={label} /> : label}
                    {required ? '*' : null}
                    {tooltip ? (
                        // biome-ignore lint/a11y/noStaticElementInteractions: suppress out of convenience during migration to biome
                        <span
                            onClick={evt => {
                                if (onTooltipClick) {
                                    onTooltipClick(evt);
                                }
                            }}
                            className={
                                'rioglyph rioglyph-question-sign cursor-pointer margin-left-5 text-size-16 text-muted'
                            }
                        />
                    ) : null}
                    {popoverTooltip && popoverTooltipMessage && (
                        <InputFieldTooltip popoverTooltipMessage={popoverTooltipMessage} trigger={'click'} />
                    )}
                </label>
                <input
                    id={finalId}
                    className={'form-control'}
                    autoComplete={
                        name === 'personalPassword' ? 'new-password' : name === 'personalEmail' ? 'username' : undefined
                    }
                    type={inputType || 'text'}
                    value={value || ''}
                    disabled={disabled}
                    name={name}
                    placeholder={placeholder}
                    title={title}
                    onChange={evt => {
                        if (onChange) {
                            onChange(evt);
                        }
                    }}
                    onBlur={evt => {
                        if (onBlur) {
                            onBlur(evt);
                        }
                    }}
                    onFocus={evt => {
                        if (onFocus) {
                            onFocus(evt);
                        }
                    }}
                />
                {error.length > 0 || warning.length > 0 ? (
                    <span className={'form-control-feedback rioglyph rioglyph-warning-sign'} />
                ) : null}
                {msgs.length > 0 ? <div className={'help-block'}>{msgs}</div> : null}
            </div>
        </div>
    );
};
